import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthSessionService } from './auth-session.service';

interface SecondScreenData {
    [key: string]: any;
}

type TabRegistry = Record<string, number>;
interface ManualSignTakeoverEvent {
    senderTabId: string;
}

@Injectable({ providedIn: 'root' })
export class SecondScreenService implements OnDestroy {
    private static readonly TAB_REGISTRY_KEY = 'qrda-app-tabs';
    private readonly router = inject(Router);
    private childWindow: Window | null = null;
    private sessionCleanup?: () => void;
    private readonly qrVisibilityStorageKey = 'manual-signature-qr-visible';
    private readonly qrVisibleSubject = new BehaviorSubject<boolean>(true);
    private readonly currentTabOwnsSessionSubject = new BehaviorSubject<boolean>(false);
    private readonly broadcastChannel: BroadcastChannel | null;
    private readonly tabId: string;
    private routerSub?: Subscription;
    private watchingNewTabs = false;
    private parentTabId = '';
    private knownTabIds = new Set<string>();
    private readonly storageEventHandler = (event: StorageEvent) => {
        if (event.key === this.qrVisibilityStorageKey) {
            this.syncQrVisibleFromStorage();
            return;
        }
        if (event.key === 'authSessionActive' && !event.newValue) {
            // Guest sign-link tabs outlive another tab's logout, so keep their manual-sign popup.
            if (!AuthSessionService.isGuestSignLinkBoot()) {
                this.closeManualSignatureOnSessionEnd();
            }
            return;
        }
        if (event.key === SecondScreenService.TAB_REGISTRY_KEY) {
            this.onTabRegistryChanged(event);
        }
    };
    private readonly beforeUnloadHandler = (): void => {
        if (!this.ownsActiveSecondScreenSession()) {
            return;
        }
        this.closeManualSignatureOnSessionEnd();
    };

    constructor() {
        this.tabId = this.resolveTabId();
        this.broadcastChannel =
            typeof BroadcastChannel !== 'undefined'
                ? new BroadcastChannel('manual-signature-qr-visibility')
                : null;
        this.broadcastChannel?.addEventListener('message', (event: MessageEvent) => {
            const message = event.data || {};
            if (message?.type === 'manual-sign-takeover') {
                this.handleTakeoverEvent(message.payload as ManualSignTakeoverEvent);
                return;
            }

            const nextValue = message?.qrVisible;
            if (typeof nextValue !== 'boolean') {
                return;
            }
            this.applyQrVisibility(nextValue, false);
        });
        window.addEventListener('storage', this.storageEventHandler);
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
        this.routerSub = this.router.events
            .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
            .subscribe((e) => {
                if (this.isLoginNavigation(e.url)) {
                    this.closeManualSignatureOnSessionEnd();
                }
            });
        this.syncQrVisibleFromStorage();
    }

    get qrVisible$(): Observable<boolean> {
        return this.qrVisibleSubject.asObservable();
    }

    get currentTabOwnsSession$(): Observable<boolean> {
        return this.currentTabOwnsSessionSubject.asObservable();
    }

    isQrVisible(): boolean {
        return this.qrVisibleSubject.value;
    }

    currentTabOwnsSession(): boolean {
        return this.currentTabOwnsSessionSubject.value;
    }

    setQrVisible(visible: boolean): void {
        this.applyQrVisibility(visible, true);
    }

    registerAppTab(tabId: string): void {
        if (AuthSessionService.isSecondScreenChild() || !tabId) {
            return;
        }
        const tabs = this.readTabRegistry();
        tabs[tabId] = Date.now();
        localStorage.setItem(SecondScreenService.TAB_REGISTRY_KEY, JSON.stringify(tabs));
    }

    unregisterAppTab(tabId: string): void {
        if (!tabId) {
            return;
        }
        const tabs = this.readTabRegistry();
        if (!tabs[tabId]) {
            return;
        }
        delete tabs[tabId];
        localStorage.setItem(SecondScreenService.TAB_REGISTRY_KEY, JSON.stringify(tabs));
    }

    ngOnDestroy() {
        console.log('[SECOND-SCREEN] Service destroyed, cleaning up...');
        this.routerSub?.unsubscribe();
        this.broadcastChannel?.close();
        window.removeEventListener('storage', this.storageEventHandler);
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        this.stopNewTabWatch();
        this.cleanUpAll();
    }

    /**
     * Session end / logout / tab close: hide QR and close the manual-signature popup window.
     * Also triggered when another tab clears `authSessionActive` (storage event) or when navigating to login.
     */
    closeManualSignatureOnSessionEnd(): void {
        this.setQrVisible(false);
        this.closeSecondScreen();
    }

    /**
     * Tab-local cleanup: only the tab that currently owns an active manual-sign session
     * is allowed to publish a global QR hide/close on unload or local teardown.
     */
    closeManualSignatureIfOwnedByCurrentTab(): void {
        if (!this.ownsActiveSecondScreenSession()) {
            return;
        }
        this.closeManualSignatureOnSessionEnd();
    }

    private isLoginNavigation(url: string): boolean {
        const path = (url || '').split('?')[0].split('#')[0];
        return path === '/login' || path === 'login' || path.endsWith('/login');
    }

    async openSecondScreen(url: string, data: SecondScreenData): Promise<SecondScreenData | null> {
        console.log('[SECOND-SCREEN] Starting new session for URL:', url);
        this.broadcastManualSignTakeover();
        this.cleanUpAll();

        return new Promise<SecondScreenData | null>(async (resolve) => {
            let pollId: any;

            const messageHandler = (event: MessageEvent) => {
                // Security Check
                if (event.origin !== window.location.origin) {
                    console.warn('[SECOND-SCREEN] Ignored message from untrusted origin:', event.origin);
                    return;
                }

                const { type, payload } = event.data || {};

                switch (type) {
                    case 'SECOND_SCREEN_READY':
                        console.log('[SECOND-SCREEN] Child window ready. Sending INIT_DATA:', data);
                        this.childWindow?.postMessage({ type: 'INIT_DATA', payload: data }, event.origin);
                        this.startNewTabWatch();
                        break;

                    case 'SECOND_SCREEN_DONE':
                        console.log('[SECOND-SCREEN] Received DONE signal with payload:', payload);
                        finalize(payload ?? null);
                        break;

                    default:
                        // Log unexpected message types for debugging
                        if (type) console.debug('[SECOND-SCREEN] Received unhandled message type:', type);
                }
            };

            const finalize = (result: SecondScreenData | null) => {
                console.log('[SECOND-SCREEN] Finalizing session. Result:', result ? 'SUCCESS' : 'CANCELLED/NULL');
                window.removeEventListener('message', messageHandler);
                this.stopNewTabWatch();
                if (pollId) clearInterval(pollId);
                this.closeChildWindow();
                this.sessionCleanup = undefined;
                this.currentTabOwnsSessionSubject.next(false);
                resolve(result);
            };

            this.sessionCleanup = () => {
                console.log('[SECOND-SCREEN] Emergency cleanup triggered.');
                finalize(null);
            };
            this.currentTabOwnsSessionSubject.next(true);

            window.addEventListener('message', messageHandler);

            try {
                console.log('[SECOND-SCREEN] Attempting to calculate best screen...');
                this.childWindow = await this.openOnBestScreen(url);

                if (!this.childWindow) {
                    console.error('[SECOND-SCREEN] Window object is null. Likely blocked by browser.');
                    finalize(null);
                    return;
                }

                console.log('[SECOND-SCREEN] Window opened successfully.');
            } catch (err) {
                console.error('[SECOND-SCREEN] Critical error opening window:', err);
                finalize(null);
                return;
            }

            // Monitor for manual window closure
            pollId = setInterval(() => {
                if (this.childWindow?.closed) {
                    console.warn('[SECOND-SCREEN] Child window was closed manually by the user.');
                    finalize(null);
                }
            }, 1000);
        });
    }

    public closeSecondScreen(): void {
        console.log('[SECOND-SCREEN] Manual close requested by parent.');
        if (this.sessionCleanup) {
            this.cleanUpAll();
        } else if (this.childWindow && !this.childWindow.closed) {
            // e.g. parent cleared document while a session reference was already torn down — still close the popup.
            this.closeChildWindow();
        }
    }

    private async openOnBestScreen(url: string): Promise<Window | null> {
        const anyWin = window as any;

        if ('getScreenDetails' in anyWin) {
            try {
                const screenDetails = await anyWin.getScreenDetails();
                console.log('[SECOND-SCREEN] Screens detected:', screenDetails.screens.length);

                if (screenDetails.screens.length > 1) {
                    const secondary = screenDetails.screens.find((s: any) => !s.isPrimary) || screenDetails.screens[1];
                    console.log('[SECOND-SCREEN] Targeting secondary screen:', secondary.label);
                    return window.open(url, '_blank', this.buildFeatures(secondary));
                }
            } catch (e) {
                console.warn('[SECOND-SCREEN] Window Management API error or denied:', e);
            }
        }

        console.log('[SECOND-SCREEN] Using fallback (primary screen).');
        return window.open(url, '_blank', this.buildFeatures(screen));
    }

    private buildFeatures(s: any): string {
        // 's' is the screen object from getScreenDetails or window.screen
        // Use .width and .height instead of .availWidth to ignore taskbars
        const width = s.width || screen.width;
        const height = s.height || screen.height;
        const left = s.availLeft ?? s.left ?? 0;
        const top = s.availTop ?? s.top ?? 0;

        return [
            'popup=yes',
            'menubar=no',
            'toolbar=no',
            'location=no',
            'status=no',
            'resizable=yes',
            'scrollbars=no',
            `left=${left}`,
            `top=${top}`,
            `width=${width}`,
            `height=${height}`,
        ].join(',');
    }

    private closeChildWindow() {
        if (this.childWindow && !this.childWindow.closed) {
            this.childWindow.close();
            console.log('[SECOND-SCREEN] Child window closed programmatically.');
        }
        this.childWindow = null;
        this.currentTabOwnsSessionSubject.next(false);
    }

    private cleanUpAll() {
        if (this.sessionCleanup) {
            // We use a local reference to avoid potential race conditions 
            // during high-frequency calls.
            const cleanup = this.sessionCleanup;
            this.sessionCleanup = undefined;
            cleanup();
        }
    }

    private startNewTabWatch(): void {
        if (this.watchingNewTabs) {
            return;
        }
        this.watchingNewTabs = true;
        this.parentTabId = sessionStorage.getItem('appTabId') ?? '';
        this.knownTabIds = new Set(Object.keys(this.readTabRegistry()));
    }

    private stopNewTabWatch(): void {
        this.watchingNewTabs = false;
        this.knownTabIds.clear();
        this.parentTabId = '';
    }

    private ownsActiveSecondScreenSession(): boolean {
        if (this.sessionCleanup) {
            return true;
        }
        if (this.childWindow && !this.childWindow.closed) {
            return true;
        }
        return this.watchingNewTabs;
    }

    private resolveTabId(): string {
        const existing = sessionStorage.getItem('appTabId') || sessionStorage.getItem('manualSignTabId');
        if (existing) {
            return existing;
        }
        const generated = `manual-sign-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        sessionStorage.setItem('manualSignTabId', generated);
        return generated;
    }

    private broadcastManualSignTakeover(): void {
        const event: ManualSignTakeoverEvent = {
            senderTabId: this.tabId,
        };
        this.broadcastChannel?.postMessage({ type: 'manual-sign-takeover', payload: event });
    }

    private handleTakeoverEvent(event: ManualSignTakeoverEvent | null | undefined): void {
        if (!event?.senderTabId) {
            return;
        }
        if (event.senderTabId === this.tabId) {
            return;
        }
        if (!this.ownsActiveSecondScreenSession()) {
            return;
        }
        // Replace previous manual-sign owner across tabs without publishing a global QR visibility reset.
        this.cleanUpAll();
        if (this.childWindow && !this.childWindow.closed) {
            this.closeChildWindow();
        }
    }

    private onTabRegistryChanged(event: StorageEvent): void {
        if (!this.watchingNewTabs || !event.newValue || !this.childWindow || this.childWindow.closed) {
            return;
        }

        let next: TabRegistry = {};
        let prev: TabRegistry = {};
        try {
            next = JSON.parse(event.newValue) as TabRegistry;
            prev = event.oldValue ? (JSON.parse(event.oldValue) as TabRegistry) : {};
        } catch {
            return;
        }

        for (const tabId of Object.keys(next)) {
            if (prev[tabId] || tabId === this.parentTabId || this.knownTabIds.has(tabId)) {
                continue;
            }
            console.log('[SECOND-SCREEN] Closing — new app tab opened:', tabId);
            this.closeManualSignatureOnSessionEnd();
            return;
        }
    }

    private readTabRegistry(): TabRegistry {
        try {
            return JSON.parse(localStorage.getItem(SecondScreenService.TAB_REGISTRY_KEY) || '{}') as TabRegistry;
        } catch {
            return {};
        }
    }

    private syncQrVisibleFromStorage(): void {
        const rawValue = localStorage.getItem(this.qrVisibilityStorageKey);
        if (rawValue === null) {
            this.qrVisibleSubject.next(true);
            return;
        }
        this.qrVisibleSubject.next(rawValue === 'true');
    }

    private applyQrVisibility(visible: boolean, publish: boolean): void {
        this.qrVisibleSubject.next(visible);
        if (!publish) {
            return;
        }
        localStorage.setItem(this.qrVisibilityStorageKey, String(visible));
        this.broadcastChannel?.postMessage({ qrVisible: visible });
    }
}
